import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../api';
import { ACTION_LABEL, Alert, Card, Empty, fmtNum, humanize, PageHeader, Spinner, useLoader } from '../components/ui';
import { ACTIONS, type Action, type Stats, type Usage } from '../types';

function Kpi({ label, value, tone, to, hint }: { label: string; value: string; tone?: string; to?: string; hint?: string }) {
  const body = (
    <>
      <span className="kpi-label">{label}</span>
      <span className={`kpi-value ${tone ? `tone-text-${tone}` : ''}`}>{value}</span>
      {hint && <span className="kpi-hint">{hint}</span>}
    </>
  );
  return to ? (
    <Link to={to} className="kpi kpi-link">
      {body}
    </Link>
  ) : (
    <div className="kpi">{body}</div>
  );
}

function UsageCard({ usage }: { usage: Usage }) {
  const { limits } = usage;
  const share = Math.min(1, usage.messages_accepted / Math.max(1, limits.monthly_messages));
  const resets = new Date(usage.period.end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return (
    <Card
      title="This month"
      subtitle={`${humanize(usage.plan)} plan · ${fmtNum(limits.messages_per_second)} messages/s · ${limits.max_decision_streams} decision streams · resets ${resets}`}
    >
      <div className="usage-meter">
        <div className="usage-meter-label">
          <span>
            {fmtNum(usage.messages_accepted)} of {fmtNum(limits.monthly_messages)} messages
          </span>
          <span className="muted small">
            {limits.overage
              ? usage.overage_messages
                ? `+${fmtNum(usage.overage_messages)} overage`
                : 'overage billed beyond this'
              : 'stops at the limit'}
          </span>
        </div>
        <div className="hbar-track">
          <div className="hbar-fill" style={{ width: `${share * 100}%` }} />
        </div>
      </div>
      <div className="kpis">
        <Kpi label="Moderated" value={fmtNum(usage.messages_moderated)} hint={`${fmtNum(usage.reprocessed)} reprocessed`} />
        <Kpi label="Reused results" value={fmtNum(usage.cache_hits)} hint="repeated content, no model call" />
        <Kpi label="Failed" value={fmtNum(usage.messages_failed)} hint="not billed" tone={usage.messages_failed ? 'bad' : undefined} />
        <Kpi
          label="Terms used"
          value={`${fmtNum(usage.terms.chars_used)} chars`}
          hint={usage.terms.chars > usage.terms.limit ? `cut from ${fmtNum(usage.terms.chars)}; plan limit ${fmtNum(usage.terms.limit)}` : `plan limit ${fmtNum(usage.terms.limit)}`}
          tone={usage.terms.chars > usage.terms.limit ? 'flag' : undefined}
        />
      </div>
    </Card>
  );
}

function dayKey(d: Date) {
  return d.toISOString().slice(0, 10);
}

function DailyChart({ stats }: { stats: Stats }) {
  const { days, max } = useMemo(() => {
    const byDay = new Map<string, Record<Action, number>>();
    const today = new Date();
    const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    for (let i = stats.days - 1; i >= 0; i--) {
      const d = new Date(start.getTime() - i * 86_400_000);
      byDay.set(dayKey(d), { allow: 0, flag: 0, block_message: 0, block_message_and_user: 0 });
    }
    for (const row of stats.daily) {
      if (!row.action) continue;
      const k = dayKey(new Date(row.day));
      const bucket = byDay.get(k);
      if (bucket) bucket[row.action] += row.n;
    }
    const list = [...byDay.entries()].map(([day, counts]) => ({
      day,
      counts,
      total: ACTIONS.reduce((s, a) => s + counts[a], 0),
    }));
    return { days: list, max: Math.max(1, ...list.map((d) => d.total)) };
  }, [stats]);

  const labelEvery = days.length > 14 ? 5 : 1;
  return (
    <div className="chart">
      <div className="chart-legend">
        {ACTIONS.map((a) => (
          <span key={a} className="legend-item">
            <span className={`legend-swatch fill-${a}`} />
            {ACTION_LABEL[a]}
          </span>
        ))}
      </div>
      <div className="chart-body">
        <div className="chart-axis">
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="chart-bars" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
          {days.map((d, i) => (
            <div key={d.day} className="chart-col" title={`${d.day}: ${d.total} messages\n${ACTIONS.map((a) => `${ACTION_LABEL[a]}: ${d.counts[a]}`).join('\n')}`}>
              <div className="chart-stack" style={{ height: `${(d.total / max) * 100}%` }}>
                {ACTIONS.map((a) =>
                  d.counts[a] ? <div key={a} className={`chart-seg fill-${a}`} style={{ flexGrow: d.counts[a] }} /> : null,
                )}
              </div>
              <span className="chart-x">
                {i % labelEvery === 0 || i === days.length - 1
                  ? new Date(d.day + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
                  : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const [days, setDays] = useState(7);
  const { data: stats, error, loading } = useLoader(() => get<Stats>('/api/stats', { days }), [days]);
  const { data: usage } = useLoader(() => get<Usage>('/api/usage'), []);

  const t = stats?.totals;
  const flaggedTotal = t ? t.flag + t.block_message + t.block_message_and_user : 0;
  const catMax = Math.max(1, ...(stats?.categories.map((c) => c.n) ?? [1]));

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`Moderation activity over the last ${days} days`}
        actions={
          <div className="segmented" role="group" aria-label="Time range">
            {[7, 30].map((d) => (
              <button key={d} className={d === days ? 'active' : ''} onClick={() => setDays(d)}>
                {d} days
              </button>
            ))}
          </div>
        }
      />
      <Alert>{error}</Alert>
      {!stats && loading && <Spinner />}
      {stats && t && (
        <>
          <div className="kpis">
            <Kpi label="Messages" value={fmtNum(t.total)} hint={`${fmtNum(t.allow)} allowed`} to="/messages?include_allowed=1" />
            <Kpi label="Flagged" value={fmtNum(t.flag)} tone="flag" to="/messages?action=flag" hint={`${fmtNum(flaggedTotal)} actioned in total`} />
            <Kpi label="Blocked messages" value={fmtNum(t.block_message)} tone="block_message" to="/messages?action=block_message" />
            <Kpi
              label="Blocked msg + user"
              value={fmtNum(t.block_message_and_user)}
              tone="block_message_and_user"
              to="/messages?action=block_message_and_user"
            />
            <Kpi label="Users blocked now" value={fmtNum(stats.blocked_users)} to="/end-users?status=blocked" />
            <Kpi label="In queue" value={fmtNum(t.in_queue)} />
            <Kpi label="Failed" value={fmtNum(t.failed)} tone={t.failed ? 'bad' : undefined} />
            <Kpi label="Avg latency" value={t.avg_latency_ms == null ? '—' : `${fmtNum(t.avg_latency_ms)} ms`} hint="received → decided" />
          </div>

          {usage && <UsageCard usage={usage} />}

          <Card title="Daily decisions" subtitle="Processed messages per day (UTC), by action">
            <DailyChart stats={stats} />
          </Card>

          <div className="grid-2">
            <Card title="Categories" subtitle="Non-allowed messages by detected category">
              {stats.categories.length === 0 ? (
                <Empty>No violations in this period.</Empty>
              ) : (
                <ul className="hbar-list">
                  {stats.categories.map((c) => (
                    <li key={c.category}>
                      <Link to={`/messages?category=${encodeURIComponent(c.category)}`} className="hbar-label">
                        {humanize(c.category)}
                      </Link>
                      <div className="hbar-track">
                        <div className="hbar-fill" style={{ width: `${(c.n / catMax) * 100}%` }} />
                      </div>
                      <span className="hbar-value mono">{c.n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Top offenders" subtitle="End users with the most non-allowed messages">
              {stats.top_offenders.length === 0 ? (
                <Empty>No offenders in this period.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table table-compact">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th className="num">Violations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_offenders.map((o) => (
                        <tr key={o.end_user_id}>
                          <td>
                            <Link to={`/messages?user_id=${encodeURIComponent(o.end_user_id)}`}>
                              <span className="mono">{o.end_user_id}</span>
                            </Link>
                            {o.end_user_name && <span className="muted"> · {o.end_user_name}</span>}
                          </td>
                          <td className="num mono">{o.violations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </>
  );
}
